import axios from 'axios';
import { getDataFromCache, setDataInCache, CACHE_TTL_MS } from '../../cache';
import * as SERVICE_CONSTANTS from '../constants/serviceConstant';
import browserUtils, { isUserLoggedIn } from '../utils/browserUtils';
import { getFunnel, getAffiliateDetails, getAuthToken, getAffiliate } from '../utils/appUtils';
import { logoutUser } from '../utils/authUtils';
import packageUtils from '../utils/packageUtils';
import { AFFILIATE_CONFIG } from '../constants/appConstant';
import { getCmpDetails } from '../src/utils/utils';
import { CMP_DETAILS_KEY } from '../src/constants';
import { getCookie } from './cookieService';
import { getUserInfoFromServer } from '../utils/getUserInfo';
import Analytics from '../../../analytics';
import { PDT_REQUEST_ID } from '../../../analytics/analytics-utils';
import { getDeviceTimezoneAndLocale } from '../utils/formattingUtils';

function getFunnelBindingKey() {
  const LS_FUNNEL_BINDING_KEY = SERVICE_CONSTANTS.FUNNEL_BINDING_KEY;
  const funnelBindingKey = getDataFromCache(LS_FUNNEL_BINDING_KEY);
  if (funnelBindingKey) {
    return funnelBindingKey.value;
  }

  const uniqueKey = browserUtils.getUniqueKey();
  setDataInCache(LS_FUNNEL_BINDING_KEY, uniqueKey, CACHE_TTL_MS.FUNNEL_KEY);
  return uniqueKey;
}

export const getHeaders = header => {
  const mmtAuth = getAuthToken();
  const visitorId = getCookie('s_vi');
  const dvid = browserUtils.getDvid();
  const correlationId = browserUtils.getUUID();
  const funnelBindingKey = getFunnelBindingKey();
  let jsessionId = browserUtils.getSessionStorage('sessionId');
  if (!jsessionId) {
    jsessionId = browserUtils.getUniqueKey();
    browserUtils.setSessionStorage('sessionId', jsessionId);
  }
  const { dk } = browserUtils.getUrlParamsMap();
  let mcid = null;
  if (ssr.isOnClient) {
    if (window.s && window.s.marketingCloudVisitorID) {
      mcid = window.s.marketingCloudVisitorID;
    } else if (window.s && window.s.visitor && window.s.visitor.b) {
      mcid = window.s.visitor.b.MCMID;
    }
  }
  //   const location = getCurrentLocationFromStorage();
  const userInfo = getUserInfoFromServer();
  const amendmentDataKey = browserUtils.getDataKey();
  const osName = browserUtils.getOSName();

  let profileType;
  if (browserUtils.isAgentLoggedIn()) {
    profileType = 'AGENT';
  }

  let call_type;
  if (browserUtils.getHostName().includes('holidaysexpert')) {
    call_type = 'agent';
  }

  const { timeZone: timezone, locale } = getDeviceTimezoneAndLocale();

  return {
    'content-type': SERVICE_CONSTANTS.HEADER_CONTENT_TYPE,
    accept: SERVICE_CONSTANTS.HEADER_ACCEPT,
    'Content-Encoding': SERVICE_CONSTANTS.HEADER_CONTENT_ENCODING,
    REQUEST_ID: browserUtils.getUUID(),
    dvc_type: SERVICE_CONSTANTS.DEVICE_TYPE,
    ...(mmtAuth && { 'mmt-auth': mmtAuth }),
    ...(visitorId && { visitorId }),
    ...(correlationId && { correlation_id: correlationId }),
    ...(jsessionId && { session_mmt_id: jsessionId }),
    ...(mcid && { mcid }),
    ...(dk && { DATA_KEY: dk }),
    /* ...(location && {
              latitude: location.latitude,
              longitude: location.longitude,
              userCity: location.userCity,
            }), */
    ...{ userAgent: ssr.isOnClient ? navigator.userAgent : ssr.userAgent },
    ...(userInfo && userInfo.uuid && { uuid: String(userInfo.uuid) }),
    ...(funnelBindingKey && { funnel_binding_key: funnelBindingKey }),
    ...(amendmentDataKey && { 'DATA-KEY': amendmentDataKey }),
    ...(timezone && { timezone }),
    ...(locale && { locale }),
    ...header,
    ...osName,
    dvid,
    ...(profileType && { profileType }),
    ...(call_type && { call_type }),
    'backend-flags': 'of=1',
  };
};

const validateAuth = res => {
  if (res?.data?.error?.code === 'USER011') {
    alert(res?.data?.error?.message || 'Authentication error. Logging you out.');
    if (isUserLoggedIn()) {
      logoutUser();
    }
  }

  return res;
};

function post(url, param, header = {}, options = {}) {
  const body = {
    channel: SERVICE_CONSTANTS.CHANNEL,
    website: SERVICE_CONSTANTS.WEBSITE,
    funnel: getFunnel(),
    affiliate: options?.affiliate ? options?.affiliate : SERVICE_CONSTANTS.AFFILIATE,
    lob: SERVICE_CONSTANTS.LOB,
    ...param,
  };
  if (param?.channel) {
    body.channel = param?.channel;
  }
  const cmp = getCmpDetails();

  if (cmp && !options.removeCtaDetails) body[CMP_DETAILS_KEY] = cmp;

  const affiliateDetails = options?.affiliate
    ? AFFILIATE_CONFIG[options?.affiliate]
    : getAffiliateDetails();
  body.affiliate = affiliateDetails.affiliateIdentifier;

  const params = {
    method: 'post',
    url: `${options.baseUrl || affiliateDetails.apiUrl}${url}`,
    data: body,
    headers: {
      [PDT_REQUEST_ID]: Analytics.getPdtRequestId(),
      ...getHeaders(header),
      journey_id: Analytics.getJourneyId(),
      REQUEST_ID: Analytics.getRequestId(),
    },
  };

  if (options.responseType) {
    params.responseType = options.responseType;
  }

  return axios(params).then(validateAuth);
}

function get(url, params = {}, header = {}, options = {}) {
  params.affiliate = getAffiliate() || SERVICE_CONSTANTS.AFFILIATE;
  params.funnel = getFunnel();
  params.channel = SERVICE_CONSTANTS.CHANNEL;
  params.website = SERVICE_CONSTANTS.WEBSITE;
  const cmp = getCmpDetails();

  // axios version upgrade issue
  // if (cmp) params[CMP_DETAILS_KEY] = cmp;

  const affiliateDetails = getAffiliateDetails();
  // eslint-disable-next-line no-param-reassign
  params.affiliate = affiliateDetails.affiliateIdentifier;

  return axios({
    method: 'get',
    url: `${affiliateDetails.apiUrl}${url}`,
    params,
    headers: {
      ...getHeaders(header),
      journey_id: Analytics.getJourneyId(),
      REQUEST_ID: Analytics.getRequestId(),
    },
  }).then(validateAuth);
}

const postSession = async (url, params = {}, header = {}) => {
  const apiResponse = await post(url, params, header);
  if (packageUtils.checkForExpiredPage(apiResponse)) {
    sessionStorage.clear();
    alert('Session expired due to inactivity. Refreshing package details');
    window.location.reload();
  } else {
    return apiResponse;
  }
};

export default { get, post, postSession };
